/**
 * Form fields for the sign-up and the profile: every control has a visible label, required marks are words
 * as well as a star, help and errors are tied to the control (aria-describedby), and an error is never a
 * colour alone (an icon and the words "Needs an answer").
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ZodError } from 'zod';
import { Icon } from '@/components/Icons';

export type Errors = Record<string, string>;

/** zod issues → { 'identity.phone': 'phone' } (the message is an i18n key under people.err). */
export function toErrors(e: ZodError | null | undefined, prefix = ''): Errors {
  const out: Errors = {};
  for (const i of e?.issues ?? []) {
    const k = [prefix, ...i.path.map(String)].filter(Boolean).join('.');
    if (!out[k]) out[k] = i.message;
  }
  return out;
}

export function useErrText() {
  const { t } = useTranslation();
  return (code: string | undefined) => {
    if (!code) return '';
    const known = ['required', 'phone', 'email', 'unit', 'date', 'emailForNotice', 'pickProgram', 'referralRegistered', 'typedName', 'agree', 'endBeforeStart', 'readyAck', 'declined'];
    if (known.includes(code)) return t(`people.err.${code}`);
    if (/Invalid enum value|Required|Expected/i.test(code)) return t('people.err.required');
    return t('people.err.generic');
  };
}

function Req({ required }: { required?: boolean }) {
  const { t } = useTranslation();
  if (!required) return null;
  return (
    <span className="req">
      {' '}
      <span aria-hidden="true">*</span>
      <span className="sr-only">{t('people.required')}</span>
    </span>
  );
}

function ErrorLine({ id, error }: { id: string; error?: string }) {
  const text = useErrText();
  if (!error) return null;
  return (
    <p className="field-err" id={id}>
      <Icon name="alert" width={16} height={16} /> {text(error)}
    </p>
  );
}

interface Base {
  id: string;
  label: ReactNode;
  help?: ReactNode;
  error?: string;
  required?: boolean;
}

export function TextField({
  id,
  label,
  help,
  error,
  required,
  value,
  onChange,
  type = 'text',
  autoComplete,
  inputMode,
  placeholder,
  maxLength,
}: Base & {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  placeholder?: string;
  maxLength?: number;
}) {
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        <Req required={required} />
      </label>
      <input
        id={id}
        className="input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={[help ? helpId : '', error ? errId : ''].filter(Boolean).join(' ') || undefined}
      />
      {help ? (
        <p className="help" id={helpId}>
          {help}
        </p>
      ) : null}
      <ErrorLine id={errId} error={error} />
    </div>
  );
}

export function TextArea({ id, label, help, error, required, value, onChange, rows = 3, maxLength }: Base & { value: string; onChange: (v: string) => void; rows?: number; maxLength?: number }) {
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        <Req required={required} />
      </label>
      <textarea
        id={id}
        className="input"
        rows={rows}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={[help ? helpId : '', error ? errId : ''].filter(Boolean).join(' ') || undefined}
      />
      {help ? (
        <p className="help" id={helpId}>
          {help}
        </p>
      ) : null}
      <ErrorLine id={errId} error={error} />
    </div>
  );
}

export function SelectField<V extends string>({
  id,
  label,
  help,
  error,
  required,
  value,
  onChange,
  options,
  blank,
}: Base & { value: V | ''; onChange: (v: V | '') => void; options: { value: V; label: string }[]; blank?: string }) {
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        <Req required={required} />
      </label>
      <select
        id={id}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value as V | '')}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={[help ? helpId : '', error ? errId : ''].filter(Boolean).join(' ') || undefined}
      >
        {blank !== undefined ? <option value="">{blank}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {help ? (
        <p className="help" id={helpId}>
          {help}
        </p>
      ) : null}
      <ErrorLine id={errId} error={error} />
    </div>
  );
}

/** One answer from a few (yes / no / not sure): radio buttons that look like the reference's segmented control. */
export function ChoiceField<V extends string>({
  id,
  label,
  help,
  error,
  required,
  value,
  onChange,
  options,
}: Base & { value: V | ''; onChange: (v: V) => void; options: { value: V; label: string }[] }) {
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  return (
    <fieldset
      className="field choice"
      aria-describedby={[help ? helpId : '', error ? errId : ''].filter(Boolean).join(' ') || undefined}
      aria-invalid={error ? true : undefined}
      data-testid={id}
    >
      <legend>
        {label}
        <Req required={required} />
      </legend>
      <div className="seg radios">
        {options.map((o) => (
          <label key={o.value} className={value === o.value ? 'on' : undefined}>
            <input type="radio" name={id} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      {help ? (
        <p className="help" id={helpId}>
          {help}
        </p>
      ) : null}
      <ErrorLine id={errId} error={error} />
    </fieldset>
  );
}

/** Select all that apply. */
export function ChecksField<V extends string>({
  id,
  label,
  help,
  error,
  required,
  values,
  onChange,
  options,
}: Base & { values: V[]; onChange: (v: V[]) => void; options: { value: V; label: string }[] }) {
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  const uid = useId();
  return (
    <fieldset className="field checks" aria-describedby={[help ? helpId : '', error ? errId : ''].filter(Boolean).join(' ') || undefined} data-testid={id}>
      <legend>
        {label}
        <Req required={required} />
      </legend>
      <div className="check-grid">
        {options.map((o) => {
          const cid = `${uid}-${o.value}`;
          const on = values.includes(o.value);
          return (
            <label key={o.value} htmlFor={cid} className={on ? 'on' : undefined}>
              <input id={cid} type="checkbox" checked={on} onChange={() => onChange(on ? values.filter((x) => x !== o.value) : [...values, o.value])} />
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
      {help ? (
        <p className="help" id={helpId}>
          {help}
        </p>
      ) : null}
      <ErrorLine id={errId} error={error} />
    </fieldset>
  );
}

export function SwitchField({ id, label, checked, onChange, help }: { id: string; label: ReactNode; checked: boolean; onChange: (v: boolean) => void; help?: ReactNode }) {
  return (
    <div className="field">
      <label className="switch" htmlFor={id}>
        <input id={id} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-describedby={help ? `${id}-help` : undefined} />
        <span>{label}</span>
      </label>
      {help ? (
        <p className="help" id={`${id}-help`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

/** Today in America/New_York as yyyy-mm-dd (the service day; never a converted UTC midnight). */
export function todayIso(): string {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const g = (k: string) => p.find((x) => x.type === k)?.value ?? '';
  return `${g('year')}-${g('month')}-${g('day')}`;
}
